<template>
  <div
    class="corner-frame"
    :class="{
      'corner-frame--border': border,
      'corner-frame--glow': glow,
      'corner-frame--circle': circle,
      'corner-frame--sized': fill || !!aspect || circle,
    }"
    :style="frameStyle"
  >
    <div class="corner-frame__content">
      <slot />
    </div>
    <span v-if="corners" class="corner-frame__corners" aria-hidden="true">
      <span class="corner-frame__corner corner-frame__corner--tl"></span>
      <span class="corner-frame__corner corner-frame__corner--tr"></span>
      <span class="corner-frame__corner corner-frame__corner--bl"></span>
      <span class="corner-frame__corner corner-frame__corner--br"></span>
    </span>
    <slot name="overlay" />
  </div>
</template>

<script setup>
/**
 * CornerFrame — рамка с четырьмя лаймовыми L-уголками поверх контента (фото, видео, аватар).
 *
 * <CornerFrame aspect="4 / 3"><img :src="url" /></CornerFrame>               // экраны 4, 6
 * <CornerFrame border glow aspect="3 / 4"><video … /></CornerFrame>          // экраны 5, 7 (светящаяся рамка)
 * <CornerFrame circle :inset="18"><img … /></CornerFrame>                    // круг (экран 3)
 * <CornerFrame fill class="my-video">…</CornerFrame>   // размер задаёт родитель (height/flex), контент растягивается
 *
 * Контент слота растягивается на всю рамку (img/video получают object-fit: cover).
 * Слот #overlay рисуется поверх уголков (оверлеи «Проверка…», овал лица и т.п.).
 *
 * props: radius (px, 22), cornerSize (px, 30), thickness (px, 3), inset (px, 16),
 *        border (лаймовая рамка 1px), glow (свечение рамки), circle (круглая), aspect (CSS aspect-ratio),
 *        fill (размер рамки задан снаружи — контент абсолютно растягивается; иначе рамка = высота контента),
 *        corners (показывать уголки, true), color (цвет уголков).
 */
import { computed } from "vue";

const props = defineProps({
  radius: { type: Number, default: 22 },
  cornerSize: { type: Number, default: 30 },
  thickness: { type: Number, default: 3 },
  inset: { type: Number, default: 16 },
  border: { type: Boolean, default: false },
  glow: { type: Boolean, default: false },
  circle: { type: Boolean, default: false },
  aspect: { type: String, default: "" },
  fill: { type: Boolean, default: false },
  corners: { type: Boolean, default: true },
  color: { type: String, default: "var(--cb-lime)" },
});

const frameStyle = computed(() => ({
  "--cf-radius": props.circle ? "50%" : `${props.radius}px`,
  "--cf-size": `${props.cornerSize}px`,
  "--cf-thick": `${props.thickness}px`,
  "--cf-inset": `${props.inset}px`,
  "--cf-color": props.color,
  "--cf-corner-radius": `${Math.max(4, Math.round(props.cornerSize / 4))}px`,
  aspectRatio: props.circle && !props.aspect ? "1 / 1" : props.aspect || undefined,
}));
</script>

<style scoped>
.corner-frame {
  position: relative;
  width: 100%;
  border-radius: var(--cf-radius);
  background: var(--cb-surface);
  isolation: isolate;
}

.corner-frame__content {
  position: relative;
  overflow: hidden;
  border-radius: inherit;
}

.corner-frame--sized .corner-frame__content {
  position: absolute;
  inset: 0;
}

.corner-frame__content > :deep(img),
.corner-frame__content > :deep(video),
.corner-frame__content > :deep(canvas) {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.corner-frame--border {
  box-shadow: 0 0 0 1px var(--cb-lime-border);
}

.corner-frame--glow {
  box-shadow: 0 0 0 1px var(--cb-lime), var(--cb-glow);
}

.corner-frame__corners {
  position: absolute;
  inset: var(--cf-inset);
  pointer-events: none;
  z-index: 2;
}

.corner-frame__corner {
  position: absolute;
  width: var(--cf-size);
  height: var(--cf-size);
  border: 0 solid var(--cf-color);
  filter: drop-shadow(0 0 4px rgba(221, 245, 90, 0.45));
}

.corner-frame__corner--tl {
  top: 0;
  left: 0;
  border-top-width: var(--cf-thick);
  border-left-width: var(--cf-thick);
  border-top-left-radius: var(--cf-corner-radius);
}

.corner-frame__corner--tr {
  top: 0;
  right: 0;
  border-top-width: var(--cf-thick);
  border-right-width: var(--cf-thick);
  border-top-right-radius: var(--cf-corner-radius);
}

.corner-frame__corner--bl {
  bottom: 0;
  left: 0;
  border-bottom-width: var(--cf-thick);
  border-left-width: var(--cf-thick);
  border-bottom-left-radius: var(--cf-corner-radius);
}

.corner-frame__corner--br {
  bottom: 0;
  right: 0;
  border-bottom-width: var(--cf-thick);
  border-right-width: var(--cf-thick);
  border-bottom-right-radius: var(--cf-corner-radius);
}

.corner-frame--circle .corner-frame__corners {
  inset: calc(var(--cf-inset) + 6%);
}
</style>
